/**
 * The reporting period, as a chip on the filter bar.
 *
 * THE PERIOD IS A FILTER, and it belongs on the bar with the rest of them. It
 * narrows the answer the same way a customer or an activity does — "for these
 * customers, in this month" is one sentence — and wherever it sat somewhere
 * else, it was the one filter every surface has and the one filter that did not
 * look or behave like a filter. The report builder had it as a band of two date
 * inputs above the bar; the dashboard had it as a calendar button beside the
 * chips. Both are this component now.
 *
 * FIRST and unremovable: every report is asked over some window, so there is no
 * state in which there is no period, only ones where the period is wider. That
 * is why this carries no ✕.
 *
 * A MACRO IS STORED AS ITSELF. Picking "Previous quarter" saves the macro and
 * the chip goes on reading "Previous quarter" — next quarter it will mean the
 * quarter after this one, which is what somebody choosing a macro asked for.
 * Only a custom range stores dates. The chip SHOWS the period that macro
 * currently resolves to alongside the name — "This month · Sep 2026" — one
 * shade back (see `PeriodValue` at the foot of this file); it is not what is
 * stored and it moves with the calendar, which is exactly why it has to be
 * visible.
 *
 * IT KEEPS A DRAFT, and that is the whole of its complexity. What the panel
 * reports is not always a window a caller can take — a caller's `problem` may
 * refuse it, and a custom value can arrive missing an end — and both callers
 * drive a live query that would blank out under somebody still choosing. So
 * the draft holds what has been picked so far, `onChange` fires only once it
 * resolves to a runnable window, and every reason it has not fired is printed
 * in the footer rather than swallowed. (The half-range between the calendar's
 * two clicks never gets this far: `DateRangePanel` holds that itself.)
 */

import { format, parse } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import { DateRangePanel } from '../date-range/date-range-panel';
import {
  type DateRangePreset,
  type DateRangeValue,
  dateRangeLabel,
  dateRangeParts,
  type ResolvedDateRange,
  resolveDateRange,
} from '../date-range/model';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';
import { FilterChip, FilterChipTrigger } from './filter-chip';

export interface PeriodChipProps {
  /** The window the surface is CURRENTLY running — never the draft. */
  value: DateRangeValue;
  /**
   * Take this window.
   *
   * Called only with a selection that resolves and that {@link problem}
   * accepted, so a caller may write it straight to a spec or a URL without a
   * guard of its own. A half-picked range never arrives here.
   */
  onChange: (next: DateRangeValue) => void;
  /** Macros to offer, in order. */
  presets: readonly DateRangePreset[];
  /**
   * The span the calendar can reach. Supplying both turns the month caption
   * into month/year dropdowns — worth it where this chip is the only way to
   * name a date, since stepping an arrow back four years is not a way.
   */
  earliest?: string;
  latest?: string;
  /**
   * Why this window cannot be taken, in a sentence, or null if it can.
   *
   * The caller's rule, not this component's: a report refuses a span past its
   * own ceiling, and nothing here knows what that ceiling is. Whatever comes
   * back is shown in the footer AND withholds the commit, so the two can never
   * disagree about whether a period was accepted.
   *
   * Handed the selection too, for a rule about HOW the window was chosen
   * rather than which days it covers — a report that refuses a custom range
   * refuses it whatever its dates.
   */
  problem?: (resolved: ResolvedDateRange, value: DateRangeValue) => string | null;
  /**
   * The window the surface is ACTUALLY asking over, when the caller resolved
   * the macro once and kept the answer.
   *
   * Without it a macro is re-answered on every render, which is right for a
   * surface that re-answers it too — the dashboard resolves `period` per
   * render, so its chip and its tiles move together — and wrong for one that
   * does not. A report builder's spec takes its `from`/`to` when the spec
   * arrives and then holds them: left open across a month boundary, the chip
   * would name October over a September report and a September export. Give
   * the dates the query is using and the chip names those instead.
   *
   * The macro's NAME is unaffected either way. A selection of "this month" is
   * still stored, still shown, and still means whatever month it is asked in
   * next.
   */
  window?: ResolvedDateRange;
  /** The chip's name. "Date" everywhere so far. */
  label?: string;
  className?: string;
}

export function PeriodChip({
  value,
  onChange,
  presets,
  earliest,
  latest,
  problem,
  window,
  label = 'Date',
  className,
}: PeriodChipProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>(value);
  // A first click on the calendar: the start of a range the panel is holding
  // until the second. Shown in the footer, which would otherwise go on naming
  // a window the calendar has stopped drawing.
  const [pending, setPending] = useState<string>();
  // The draft follows the committed value when it changes underneath — a saved
  // report loading, a reset, a drill — so this stays a VIEW of that value
  // rather than a second copy of it that can drift.
  const [seen, setSeen] = useState(value);
  if (seen.preset !== value.preset || seen.from !== value.from || seen.to !== value.to) {
    setSeen(value);
    setDraft(value);
    setPending(undefined);
  }

  const resolved = resolveDateRange(draft);
  const why = !resolved
    ? draft.preset === 'custom'
      ? 'Pick both ends of the range.'
      : 'That period has no dates.'
    : (problem?.(resolved, draft) ?? null);

  /**
   * Was the last thing picked actually taken?
   *
   * A ref, because the only reader is {@link DateRangePanel}'s `onPresetPicked`
   * — which fires in the same event as the `onChange` that decided it, a render
   * before state would have caught up. A macro closes the panel; a macro this
   * caller REFUSES must not, or the panel takes the reason away with it and the
   * pick is dropped in silence. That is what "previous quarter" did against a
   * report whose ceiling the quarter exceeded.
   */
  const accepted = useRef(true);

  const change = useCallback(
    (next: DateRangeValue) => {
      setDraft(next);
      const r = resolveDateRange(next);
      accepted.current = !!r && !problem?.(r, next);
      if (accepted.current) onChange(next);
    },
    [onChange, problem],
  );

  // OPEN, the chip reads the draft — what is being picked, live under the
  // pointer. CLOSED, it reads the committed value, because a draft that was
  // refused is not what the surface is running: a half-picked range left behind
  // by a click outside would otherwise leave the bar saying "Custom range" over
  // a report still covering last month. Reopening re-seeds for the same reason.
  const toggle = (next: boolean) => {
    if (next) setDraft(value);
    setPending(undefined);
    setOpen(next);
  };

  // WHAT IS ON SCREEN, and whether the caller's window still describes it.
  // Opening seeds the draft with `value` itself, so identity is the exact test
  // for "nothing has been picked yet": until the first pick the chip is still
  // showing the selection the surface is running, and `window` is that
  // selection's window. After a pick it is somebody else's — a macro picked
  // here resolves now, by definition — so it is dropped.
  const shown = open ? draft : value;
  const running = shown === value ? window : undefined;

  return (
    <Popover open={open} onOpenChange={toggle}>
      <FilterChip active filterId="period" className={cn('pr-1.5', className)}>
        <PopoverTrigger asChild>
          <FilterChipTrigger
            name={label}
            value={<PeriodValue value={shown} running={running} />}
            expanded={open}
            title="The window this covers"
          />
        </PopoverTrigger>
      </FilterChip>

      <PopoverContent data-picker="date" className="w-auto p-0" align="start" aria-label={label}>
        <DateRangePanel
          value={draft}
          presets={presets}
          earliest={earliest}
          latest={latest}
          onChange={change}
          onPendingChange={setPending}
          // Only a macro closes the panel, and only one that was TAKEN.
          // Custom still needs two clicks on the calendar, and a panel that
          // shut after the first would take the half-range with it; a refused
          // macro leaves the panel up so its reason is there to read.
          onPresetPicked={(_, shouldClose) => {
            if (shouldClose && accepted.current) setOpen(false);
          }}
        />
        {/* THE WINDOW IN FULL, which is the half the chip gives up to stay
            narrow: the bar says "This month · Sep 2026" and this says
            "Sep 1 – Sep 30, 2026". The two are the same window and the panel
            is where there is room to spell it out, so nothing is behind a
            click that a reader has to guess at. A reason to refuse takes the
            line, because that is the more urgent thing to say about a window
            that is not going to be taken. */}
        <footer className="flex items-center justify-between gap-2 border-t px-2.5 py-1.5 text-[10.5px] text-muted-foreground">
          {pending ? (
            <span>
              From {format(parse(pending, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')} — pick the end
              date.
            </span>
          ) : (
            <>
              {why ? <span className="text-destructive">{why}</span> : null}
              <span className={cn(why && 'sr-only')}>{dateRangeLabel(draft, true, running)}</span>
            </>
          )}
        </footer>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The chip's value: the macro, and the window it means today.
 *
 * BOTH, where before there was only the macro. "Current month" is what was
 * chosen and stays the honest label for it, but it does not say which month —
 * and a reader checking whether a number covers what they think it covers had
 * to open the panel to find out, on the one filter whose value is never
 * spelled out anywhere else on the page.
 *
 * THE WINDOW IS NAMED, NOT SPELLED OUT: "This month · Sep 2026", where the
 * panel says "Sep 1 – Sep 30, 2026". A macro always resolves to a whole
 * calendar period, so the two boundary dates are the period's name said twice
 * — and the full form made this the widest chip on a bar, wider than the two
 * beside it put together. Where a window is NOT a whole period the short form
 * declines to exist and both dates are printed; see `wholePeriodLabel`.
 *
 * The window is drawn a shade back from the macro rather than beside it in the
 * same ink. It is the DERIVED half: the macro is the answer to "what did you
 * ask for", the dates are only what that came to this month, and typography is
 * what keeps the second from competing with the first on a bar being scanned.
 * A Custom pick has no macro, so its dates are the value itself — printed in
 * full, at full weight, because "Sep 2026" on its own could not be told from
 * the macro that re-answers itself every month.
 */
function PeriodValue({ value, running }: { value: DateRangeValue; running?: ResolvedDateRange }) {
  const { name, short } = dateRangeParts(value, running);
  if (!name) return short;
  if (!short) return name;
  return (
    <>
      {name}
      <span className="text-muted-foreground/80"> · {short}</span>
    </>
  );
}
